import React, {useCallback, useState} from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import styled, { keyframes } from 'styled-components';
import _ from 'lodash';
import { Helmet } from 'react-helmet';
import { Row, Column } from 'react-foundation';
import { Link, useLocation } from 'react-router-dom';
import { Error } from '../../components/Error';
import { Button } from '../../components/Button';
import { selectErrors } from '../../modules/global/selectors';
import simEmpty from '../../static/images/moi-sim-empty.png';
import simEmptyRedLine from '../../static/images/sim-card-empty-red-line.png';
import simCardBallOk from '../../static/images/sim-ok-pallo.png';
import simCardBallError from '../../static/images/sim-error-pallo.png';
// @ts-ignore
import { fadeIn } from 'react-animations';
import { Icon } from '../../components/Icon';
import { getAccountId } from '../../utils/helpers';
import * as qs from 'qs';
import { ApiRequest } from "../../utils/api";
import { useApiMutation, useApiQuery } from "../../utils/apiHooks";
import { OfferingType } from '../../utils/offeringType';
import { VerifyWithPasswordModal } from "../../components/VerifyWithPasswordModal";

interface Props {
	errors: any[];
	offeringType: OfferingType;
}

const fadeAnimation = keyframes`${fadeIn}`;

const Center = styled.div`
	font-size: 65px;
	text-align: center;
`;

const Header = styled.div`
	font-size: 3rem;
`;

const NumberInput = styled.input`
	max-width: 22rem;
	margin: auto;
	margin-bottom: 2rem;
`;

const InfoContainer = styled.div`
	h4{
		font-size: 1.4rem;
	}
	text-align: center;
	max-width: 24rem;
	margin: auto;
`;

const FadeContainer = styled.div`
	animation: 0.5s ${fadeAnimation};
`;

const SimCardContainer = styled.div<{ redLine?: boolean }>`
	height: 233px;
	width: 368px;
	margin: auto;
	background: url('${(props) => props.redLine ? simEmptyRedLine : simEmpty}') no-repeat;
	position: relative;
	text-align: center;
	.button {
		position: relative;
		top: 82px;
	}
`;

const SimCardBallIcon = styled.div<{ error?: boolean }>`
	position: relative;
	top: 35px;
	left: 2px;
	width: 200px;
	padding-top: 130px;
	padding-left: 40px;
	margin: auto;
	color: ${(props) => props.error ? '#666666' : '#93cb12'};
	font-weight: bold;
	font-size: 18px;
	letter-spacing: -0.25px;
	background: url('${(props) => props.error ? simCardBallError : simCardBallOk}') center no-repeat;
`;

const SimNumber = styled.div`
	color: #666;
	text-align: center;
	position: relative;
	top: 54px;
	font-size: 14px;
	font-weight: bold;
	letter-spacing: 1px;
	font-family: 'courier new', courier, 'Platform-web', 'Platform', 'Courier';
`;

export const SwitchSimCardPageImpl: React.FC<Props> = (props: Props) => {

	const MIN_SEARCH_TERM_LENGTH = 5;

	const [searchTerm, setSearchTerm] = useState<string>('');
	const [matchingEmptySim, setMatchingEmptySim] = useState<any | undefined>(undefined);
	const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
	const [passwordModalOpen, setPasswordModalOpen] = useState(false);
	const location = useLocation();
	const { subscriptionNumber, currentSimId }: any = qs.parse(location.search, { ignoreQueryPrefix: true });

	const { data: emptySims } = useApiQuery(ApiRequest.getEmptySims(getAccountId()));
	const [switchSim, { isSuccess: switchSimSuccess }] = useApiMutation(
		(data: {simId: string, password: string}) =>
				ApiRequest.switchSimCard(subscriptionNumber!, data.simId, data.password)
	);

	const getEmptySim = (searchTerm: string) => {
		if ((emptySims !== undefined) && (searchTerm.length >= MIN_SEARCH_TERM_LENGTH)) {
			return emptySims.find(item => _.endsWith(item.simId, searchTerm));
		} else {
			return undefined;
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = e.target.value;
		setSearchTerm(inputValue);
		setMatchingEmptySim(getEmptySim(inputValue));
	};

	const changeSimCard = () => {
		setPasswordModalOpen(true);
	};

	const handlePasswordModalClose = useCallback(() => {
		setPasswordModalOpen(false);
	}, [])

	const handlePasswordModalConfirm = useCallback((password) => {
		const simId = matchingEmptySim.simId;
		setPasswordModalOpen(false);
		setFormSubmitted(true);
		switchSim({ simId, password });
	}, [matchingEmptySim, switchSim])

	const { errors } = props;
	const ready = emptySims !== undefined;
	const noErrors = errors && errors.length < 1;
	return (
		<article>
			<Helmet
				title="Vaihda sim-korttia"
				meta={[
					{ name: 'description', content: 'Account' },
				]}
			/>
			{ready && noErrors && (
				<div>
					<Row>
						<Column className="large-8 large-centered columns" style={{marginTop: "2rem"}}>
							<Center>
								<Icon name="medium--sims" />
								<Header>Vaihda sim-kortti liittymään {currentSimId}</Header>
							</Center>
						</Column>
					</Row>

					{!formSubmitted && emptySims!.length > 0 && (
						<Row>
							<Column className="large-7 large-centered columns">
								<p>
									1. Poimi käteesi käyttämätön sim-kortti.<br />
									2. Syötä sim-kortin takapuolelta 19-numeron tunnisteesta viisi viimeistä numeroa.<br />
									3. Klikkaa ‘Ota liittymä käyttöön’.<br />
									4. Laita uusi sim-kortti laitteeseen. Homma hoidettu. Ta-daa!
								</p>
								<p>Varastossasi on {emptySims!.length} {emptySims!.length === 1 ? ' tyhjä simmi' : ' tyhjää simmiä'} </p>
							</Column>
							<Center>
								<h4>Syötä simmin tunnisteesta viisi viimeistä numeroa</h4>
								<Column className="large-6 large-centered columns">
									<NumberInput type="text" placeholder="Esim. 01250" onChange={handleChange} />
								</Column>
							</Center>
						</Row>
					)}
					{(matchingEmptySim !== undefined) && !formSubmitted && (
						<div>
							<SimCardContainer>
								<SimNumber>12345678901234xxxxx</SimNumber>
								<SimCardBallIcon><div>Uusi simmi löytyi.</div></SimCardBallIcon>
							</SimCardContainer>
							<Button onClick={changeSimCard}>
								Ota sim-kortti käyttöön
							</Button>
						</div>
					)}

					{emptySims!.length === 0 && (
						<InfoContainer>
							<h4>Yritykselläsi ei näytä olevan tyhjiä simmejä. Ei hätää, tilaa niitä helposti lisää!</h4>
							<Link to="/account/orderMore">
								<Button>
									Tilaa lisää simmejä varastoon
								</Button>
							</Link>
						</InfoContainer>
					)}

					{(matchingEmptySim === undefined) && !formSubmitted && (searchTerm.length >= MIN_SEARCH_TERM_LENGTH) && (
						<FadeContainer>
							<SimCardContainer>
								<SimNumber>12345678901234xxxxx</SimNumber>
								<SimCardBallIcon error />
							</SimCardContainer>

							<InfoContainer>
								<h4>Hupsis. Tällä tunnistenumerolla ei löytynyt käyttämätöntä simmiä.</h4>
								<p>Mitä seuraavaksi? Poimi käteesi käyttämätön sim-kortti. Syötä sim-kortin takapuolelta 19-numeron tunnisteesta viisi viimeistä numeroa.</p>
							</InfoContainer>
						</FadeContainer>
					)}

					{(matchingEmptySim === undefined) && !formSubmitted && (searchTerm.length < MIN_SEARCH_TERM_LENGTH) && emptySims!.length > 0 && (
						<FadeContainer>
							<SimCardContainer redLine>
								<SimNumber>12345678901234xxxxx</SimNumber>
							</SimCardContainer>
						</FadeContainer>
					)}
					{ready && switchSimSuccess && (
						<InfoContainer>
							<h4>Sim-kortti vaihdettu onnistuneesti!</h4>
							<Icon name="medium--ok" />
						</InfoContainer>
					)}
				</div>
			)}
			{!noErrors && (
				<Error message={'Jotain meni vikaan. Yritä myöhemmin uudestaan tai ota yhteyttä asiakaspalveluumme'} />
			)}
			<VerifyWithPasswordModal
				isOpen={passwordModalOpen}
				title="Vaihda sim-kortti"
				subtitle="Vahvista salasanalla toimenpide"
				onRequestClose={handlePasswordModalClose}
				onConfirm={handlePasswordModalConfirm}
			/>
		</article>
	);
};

const mapStateToProps = createStructuredSelector({
	errors: selectErrors,
});

export default connect(mapStateToProps)(SwitchSimCardPageImpl);

